import {
  HttpClient, HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { apiConfiguration } from '../../../../configurations/api-configuration';
import { AccessService } from '../../../application/services/AccessService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import {
  DashboardUser,
  PanelLog,
  RefreshApiTokenResponse,
  SaveUserRequest,
} from '../../types/User';

@Injectable()
export class UserManagementApiService {
  private readonly usersUrl = `${apiConfiguration.baseApiUrl}/users`;

  public constructor(
    private http: HttpClient,
    private accessService: AccessService,
  ) { }

  public getUsers(): Promise<HttpResponse<DashboardUser[]>> {
    return lastValueFrom(this.http.get<HttpResponse<DashboardUser[]>>(this.usersUrl, { headers: this.getHeaders() }));
  }

  public getDirectoryUsers(): Promise<HttpResponse<DashboardUser[]>> {
    return lastValueFrom(this.http.get<HttpResponse<DashboardUser[]>>(`${this.usersUrl}/directory`, { headers: this.getHeaders() }));
  }

  public getMe(): Promise<HttpResponse<DashboardUser>> {
    return lastValueFrom(this.http.get<HttpResponse<DashboardUser>>(`${this.usersUrl}/me`, { headers: this.getHeaders() }));
  }

  public updateMe(displayName: string | null): Promise<HttpResponse<DashboardUser>> {
    return lastValueFrom(this.http.patch<HttpResponse<DashboardUser>>(`${this.usersUrl}/me`, { displayName }, { headers: this.getHeaders() }));
  }

  public changeOwnPassword(currentPassword: string, nextPassword: string): Promise<HttpResponse<DashboardUser>> {
    return lastValueFrom(this.http.post<HttpResponse<DashboardUser>>(`${this.usersUrl}/me/password`, {
      currentPassword,
      nextPassword,
    }, { headers: this.getHeaders() }));
  }

  public refreshApiToken(): Promise<HttpResponse<RefreshApiTokenResponse>> {
    return lastValueFrom(this.http.post<HttpResponse<RefreshApiTokenResponse>>(`${this.usersUrl}/me/api-token`, {}, { headers: this.getHeaders() }));
  }

  public getPanelLogs(limit = 50): Promise<HttpResponse<PanelLog[]>> {
    return lastValueFrom(this.http.get<HttpResponse<PanelLog[]>>(`${this.usersUrl}/logs?limit=${limit}`, { headers: this.getHeaders() }));
  }

  public createUser(data: SaveUserRequest): Promise<HttpResponse<DashboardUser>> {
    return lastValueFrom(this.http.post<HttpResponse<DashboardUser>>(this.usersUrl, data, { headers: this.getHeaders() }));
  }

  public updateUser(userId: string, data: SaveUserRequest): Promise<HttpResponse<DashboardUser>> {
    return lastValueFrom(this.http.patch<HttpResponse<DashboardUser>>(`${this.usersUrl}/${userId}`, data, { headers: this.getHeaders() }));
  }

  public changeUserPassword(userId: string, password: string): Promise<HttpResponse<DashboardUser>> {
    return lastValueFrom(this.http.post<HttpResponse<DashboardUser>>(`${this.usersUrl}/${userId}/password`, { password }, { headers: this.getHeaders() }));
  }

  private getHeaders(): HttpHeaders {
    const token = this.accessService.getToken();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }
}
